import { useEffect, useState } from 'react';
import { Link, useParams } from "react-router-dom";
import './styles/practice.scss'
import PageHeader from '../../components/PageHeader';
import QuizRunner from '../../components/QuizRunner';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import { loadFaculties as fetchFacultiesCached, getCachedFaculties } from '../../facultiesCache';

type Question = { id: string; text: string; imageUrl?: string | null; type: number; topic: string; difficulty: number; options: { id: string; text: string }[] };
type RunConfig = { label: string; mode: number; subject: number | null; timeLimit: number | null; questions: Question[] };
type SavedRun = RunConfig & { idx: number; answers: { [qid: string]: string[] } };

const SUBJECTS = [{ id: 0, name: "Fyzika" }, { id: 1, name: "Chemie" }, { id: 2, name: "Biologie" }];
const SUBJECT_NAMES: { [k: number]: string } = { 0: "Fyzika", 1: "Chemie", 2: "Biologie" };
const SUBJECT_COURSE: { [k: number]: string } = { 0: "physics", 1: "chemistry", 2: "biology" };
const RUN_KEY = "mymed_practice_run";
const attemptKey = (facultyId: string) => "mymed_model_attempt_" + facultyId;
const groupKey = (facultyId: string) => "mymed_model_group_" + facultyId;

export default function Practice() {
    const { lessonId } = useParams();
    const [view, setView] = useState<"hub" | "lessonPick" | "subjectPick" | "modelPlan" | "run" | "result" | "stats">("hub");
    const [pickMode, setPickMode] = useState<"model" | "free">("model");
    const [pickSubject, setPickSubject] = useState(0);
    const [lessons, setLessons] = useState<{ number: number; title: string; lessonId: string }[]>([]);
    const [run, setRun] = useState<RunConfig | null>(null);
    const [resumeFrom, setResumeFrom] = useState<SavedRun | null>(null);
    const [result, setResult] = useState<any>(null);
    const [stats, setStats] = useState<any>(null);
    const [saved, setSaved] = useState<SavedRun | null>(null);
    const [faculties, setFaculties] = useState<{ id: string; name: string; testModel: number; testLength: number }[]>(getCachedFaculties() ?? []);
    const [freeSubject, setFreeSubject] = useState(0);
    const [freeOnlyCompleted, setFreeOnlyCompleted] = useState(false);
    const [freeFaculty, setFreeFaculty] = useState("");
    const [modelPlan, setModelPlan] = useState<{ facultyId: string; facultyName: string; subjects: { subject: number; count: number; timeMinutes: number; available: number }[] } | null>(null);
    const [attempt, setAttempt] = useState<{ [subject: number]: { correct: number; total: number } }>({});
    const [modelLog, setModelLog] = useState<{ correct: number; total: number; items: { subject: number; correct: number; total: number }[] }[]>([]);
    const [groupId, setGroupId] = useState("");

    const api = new ApiHelper();

    const MODEL_LABEL: { [k: number]: string } = { 0: "jednoduchý výběr", 1: "jednoduchý výběr + „žádná\"", 2: "kombinovaný" };

    useEffect(() => {
        const raw = localStorage.getItem(RUN_KEY);
        if (raw) { try { setSaved(JSON.parse(raw)); } catch { } }
        loadStats();
        loadFaculties();
        if (lessonId) startLessonQuiz(lessonId, null);
    }, []);

    async function loadFaculties() {
        try {
            setFaculties(await fetchFacultiesCached(api));
        } catch (x: any) { api.processErrors(x); }
    }

    async function loadStats() {
        try {
            const res = await api.practiceApi.getStatsList();
            setStats(await readJson(res));
        } catch (x: any) { api.processErrors(x); }
    }

    function persist(cfg: RunConfig, idx: number, answers: { [qid: string]: string[] }) {
        localStorage.setItem(RUN_KEY, JSON.stringify({ ...cfg, idx, answers }));
    }
    function clearRun() { localStorage.removeItem(RUN_KEY); setSaved(null); }

    function begin(cfg: RunConfig, from: SavedRun | null = null) {
        setRun(cfg);
        setResumeFrom(from);
        persist(cfg, from?.idx ?? 0, from?.answers ?? {});
        setView("run");
    }

    async function startLessonQuiz(id: string, subject: number | null) {
        try {
            const res = await api.practiceApi.getLessonQuizDetail(id);
            const data = await readJson(res);
            if (!data.questions || data.questions.length === 0) { api.alert.info("K této lekci zatím nejsou žádné otázky."); return; }
            begin({ label: "Kvíz lekce", mode: 0, subject, timeLimit: null, questions: data.questions });
        } catch (x: any) { api.processErrors(x); }
    }

    async function loadModelHistory(facultyId: string, currentGroup: string) {
        // The protocol of past sittings is server-side now; the in-progress sitting
        // (currentGroup) is shown in the attempt bar instead, so exclude it here.
        try {
            const res = await api.practiceApi.getModelHistoryList({ facultyId });
            const sittings = await readJson(res);
            setModelLog((sittings ?? []).filter((s: any) => s.groupId !== currentGroup));
        } catch { setModelLog([]); }
    }

    async function openModelPlan(facultyId: string) {
        try {
            const res = await api.practiceApi.getModelPlanList({ facultyId });
            const data = await readJson(res);
            if (!data.subjects || data.subjects.length === 0) { api.alert.info("Tato fakulta nemá nastavený model testu."); return; }
            setModelPlan({ facultyId, facultyName: data.facultyName, subjects: data.subjects });
            try { setAttempt(JSON.parse(localStorage.getItem(attemptKey(facultyId)) || "{}")); } catch { setAttempt({}); }
            let g = localStorage.getItem(groupKey(facultyId));
            if (!g) { g = crypto.randomUUID(); localStorage.setItem(groupKey(facultyId), g); }
            setGroupId(g);
            await loadModelHistory(facultyId, g);
            setView("modelPlan");
        } catch (x: any) { api.processErrors(x); }
    }

    async function startModelSubject(subject: number) {
        if (!modelPlan) return;
        try {
            const res = await api.practiceApi.getModelTestList({ facultyId: modelPlan.facultyId, subject });
            const data = await readJson(res);
            if (!data.questions || data.questions.length === 0) { api.alert.info("Pro tento předmět zatím nejsou otázky."); return; }
            begin({ label: "Modelový test – " + (SUBJECT_NAMES[subject] ?? ""), mode: 2, subject, timeLimit: data.timeLimitSeconds ?? null, questions: data.questions });
        } catch (x: any) { api.processErrors(x); }
    }

    async function startFree() {
        try {
            const res = await api.practiceApi.getFreeTestList({ subject: freeSubject, onlyCompleted: freeOnlyCompleted, facultyId: freeFaculty || undefined });
            const data = await readJson(res);
            if (!data.questions || data.questions.length === 0) { api.alert.info("Pro tento výběr nejsou žádné otázky."); return; }
            begin({ label: "Volný test", mode: 1, subject: freeSubject, timeLimit: null, questions: data.questions });
        } catch (x: any) { api.processErrors(x); }
    }

    async function openLessonPick(subject: number) {
        setPickSubject(subject);
        try {
            const res = await api.lessonsApi.getLessonsDetail((SUBJECT_COURSE[subject] ?? "physics") as any);
            const data = await readJson(res);
            setLessons(data.lessons ?? []);
            setView("lessonPick");
        } catch (x: any) { api.processErrors(x); }
    }

    async function finishRun(answers: { questionId: string; selectedOptionIds: string[] }[]) {
        if (!run) return;
        try {
            const payload: any = { mode: run.mode, subject: run.subject, answers };
            if (run.mode === 2 && modelPlan) { payload.facultyId = modelPlan.facultyId; payload.groupId = groupId; }
            const res = await api.practiceApi.submitCreate(payload);
            const r = await readJson(res);
            setResult(r);
            clearRun();
            if (run.mode === 2 && run.subject != null && modelPlan) {
                const next = { ...attempt, [run.subject]: { correct: r.correct, total: r.total } };
                setAttempt(next);
                localStorage.setItem(attemptKey(modelPlan.facultyId), JSON.stringify(next));
            }
            setView("result");
        } catch (x: any) { api.processErrors(x); }
    }

    async function openStats() {
        await loadStats();
        setView("stats");
    }

    const optText = (qid: string, oid: string) => run?.questions.find(q => q.id === qid)?.options.find(o => o.id === oid)?.text ?? "";

    if (view === "run" && run) {
        return (
            <div className="practice">
                <QuizRunner
                    questions={run.questions}
                    timeLimitSeconds={run.timeLimit}
                    initialIdx={resumeFrom?.idx ?? 0}
                    initialAnswers={resumeFrom?.answers ?? {}}
                    subject={run.subject}
                    onProgress={(idx, answers) => persist(run, idx, answers)}
                    onFinish={finishRun}
                    onQuit={() => { clearRun(); setView(run.mode === 2 && modelPlan ? "modelPlan" : "hub"); }}
                />
            </div>
        );
    }

    return (
        <div className="practice">
            <PageHeader title="Procvičování" subtitle="Kvízy, modelové testy a tvoje statistiky" />

            {view === "hub" &&
                <>
                    {saved &&
                        <div className="resumeBar">
                            <span>Máš rozpracovaný {saved.label.toLowerCase()} ({Object.keys(saved.answers).length}/{saved.questions.length} otázek).</span>
                            <div className="resumeActions">
                                <div className="rBtn" onClick={() => begin(saved, saved)}>Pokračovat</div>
                                <div className="rBtn ghost" onClick={clearRun}>Zahodit</div>
                            </div>
                        </div>
                    }
                    <div className="hubGrid">
                        <div className="hubCard" onClick={() => openLessonPick(0)}>
                            <h3>Kvízy podle lekce</h3>
                            <p>Procvič si konkrétní lekci.</p>
                        </div>
                        <div className="hubCard" onClick={() => { setPickMode("model"); setView("subjectPick"); }}>
                            <h3>Modelový test</h3>
                            <p>Časovaná simulace přijímaček.</p>
                        </div>
                        <div className="hubCard" onClick={() => { setPickMode("free"); setView("subjectPick"); }}>
                            <h3>Volný test</h3>
                            <p>Bez času, podle předmětu.</p>
                        </div>
                        <div className="hubCard" onClick={openStats}>
                            <h3>Statistiky</h3>
                            <p>Tvůj pokrok a slabá místa.</p>
                        </div>
                    </div>

                    {stats &&
                        <div className="card hubStats">
                            <h2 className="cardTitle">Tvůj přehled</h2>
                            {stats.attemptsCount === 0 ?
                                <p className="muted">Zatím jsi nevyplnil žádný kvíz. Pusť se do procvičování a tady uvidíš svůj pokrok a slabá místa.</p>
                                :
                                <>
                                    <div className="statTop">
                                        <div className="s"><span className="n">{stats.attemptsCount}</span><span className="l">vyřešených kvízů</span></div>
                                        <div className="s"><span className="n">{stats.averageScore}%</span><span className="l">průměrná úspěšnost</span></div>
                                        <div className="s"><span className="n">{stats.weakSpots.length}</span><span className="l">slabých témat</span></div>
                                    </div>
                                    {stats.topics.length > 0 &&
                                        <div className="topics">
                                            {stats.topics.slice(0, 4).map((t: any, i: number) => (
                                                <div key={i} className="topic">
                                                    <span className="tName">{t.topic}</span>
                                                    <div className="tBar"><div className={"tFill " + (t.successPercent >= 60 ? "good" : "bad")} style={{ width: t.successPercent + "%" }} /></div>
                                                    <span className="tPct">{t.successPercent}%</span>
                                                </div>
                                            ))}
                                        </div>
                                    }
                                    <div className="qBtn ghost hubStatsBtn" onClick={openStats}>Všechny statistiky</div>
                                </>
                            }
                        </div>
                    }
                </>
            }

            {view === "subjectPick" && pickMode === "model" &&
                <div className="card pick">
                    <Link to="#" className="backLink" onClick={(e) => { e.preventDefault(); setView("hub"); }}>← Zpět</Link>
                    <h2 className="cardTitle">Modelový test — vyber fakultu</h2>
                    <p className="muted">Test se sestaví podle modelu dané fakulty (typ otázek, počet a čas po předmětech).</p>
                    <div className="facultyList">
                        {faculties.filter(f => f.testLength > 0).map(f => (
                            <div key={f.id} className="facultyRow" onClick={() => openModelPlan(f.id)}>
                                <div className="fInfo">
                                    <h3>{f.name}</h3>
                                    <span>{MODEL_LABEL[f.testModel]} · {f.testLength} otázek</span>
                                </div>
                                <span className="fGo">Vybrat →</span>
                            </div>
                        ))}
                    </div>
                </div>
            }

            {view === "modelPlan" && modelPlan && (() => {
                const attemptSubjects = Object.keys(attempt).map(Number);
                const attemptCorrect = attemptSubjects.reduce((a, s) => a + attempt[s].correct, 0);
                const attemptTotal = attemptSubjects.reduce((a, s) => a + attempt[s].total, 0);

                function newTest() {
                    // The finished sitting is already persisted server-side (each subject run
                    // was submitted with the current group); start a fresh group and reload.
                    const ng = crypto.randomUUID();
                    setGroupId(ng);
                    localStorage.setItem(groupKey(modelPlan!.facultyId), ng);
                    setAttempt({});
                    localStorage.removeItem(attemptKey(modelPlan!.facultyId));
                    loadModelHistory(modelPlan!.facultyId, ng);
                }

                return (
                    <div className="card pick">
                        <Link to="#" className="backLink" onClick={(e) => { e.preventDefault(); setView("subjectPick"); }}>← Zpět na fakulty</Link>
                        <h2 className="cardTitle">{modelPlan.facultyName}</h2>
                        <p className="muted lead">Každý předmět vyplň zvlášť (vlastní časový limit). Tlačítkem „Spustit nový test" uložíš celkový výsledek pokusu do protokolu a začneš znovu.</p>

                        <div className="facultyList">
                            {modelPlan.subjects.map(s => {
                                const r = attempt[s.subject];
                                return (
                                    <div key={s.subject} className={"facultyRow" + (r ? " done" : "")} onClick={() => startModelSubject(s.subject)}>
                                        <div className="fInfo">
                                            <h3>{SUBJECT_NAMES[s.subject] ?? "Předmět"}</h3>
                                            <span>{r ? r.correct + "/" + r.total + " správně"
                                                : (s.available < s.count
                                                    ? "Jen " + s.available + " z " + s.count + " otázek · " + s.timeMinutes + " min"
                                                    : s.count + " otázek · " + s.timeMinutes + " min")}</span>
                                        </div>
                                        <span className="fGo">{r ? "✓ znovu" : "Spustit →"}</span>
                                    </div>
                                );
                            })}
                        </div>

                        {attemptSubjects.length > 0 &&
                            <div className="attemptBar">
                                <span>Tento pokus: <b>{attemptCorrect}/{attemptTotal} správně</b> ({attemptSubjects.length}/{modelPlan.subjects.length} předmětů)</span>
                                <div className="qBtn newTest" onClick={newTest}>Uložit a spustit nový test</div>
                            </div>
                        }

                        {modelLog.length > 0 &&
                            <div className="resultLog">
                                <h3 className="statH">Protokol pokusů</h3>
                                <div className="logList">
                                    {modelLog.map((e, i) => {
                                        const pct = e.total > 0 ? Math.round(100 * e.correct / e.total) : 0;
                                        return (
                                            <div key={i} className={"logEntry " + (pct >= 60 ? "good" : "bad")}>
                                                <div className="logRow">
                                                    <span className="lAttempt">Pokus {i + 1}</span>
                                                    <span className="lScore"><b>{e.correct}/{e.total}</b> správně<span className="lPct">{pct} %</span></span>
                                                </div>
                                                <div className="logSubjects">
                                                    {(e.items ?? []).map((it, j) => {
                                                        const sp = it.total > 0 ? Math.round(100 * it.correct / it.total) : 0;
                                                        return (
                                                            <span key={j} className={"logSubj " + (sp >= 60 ? "ok" : "low")}>
                                                                <span className="dot" />{SUBJECT_NAMES[it.subject] ?? "Předmět"} <b>{it.correct}/{it.total}</b>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        }
                    </div>
                );
            })()}

            {view === "subjectPick" && pickMode === "free" &&
                <div className="card pick freeTest">
                    <Link to="#" className="backLink" onClick={(e) => { e.preventDefault(); setView("hub"); }}>← Zpět</Link>
                    <h2 className="cardTitle">Volný test</h2>
                    <p className="muted lead">Procvičuj neomezeně náhodné otázky z vybraného předmětu.</p>

                    <div className="field">
                        <label className="pickLabel">Předmět</label>
                        <div className="subjectCards">
                            {SUBJECTS.map(s => (
                                <div key={s.id} className={"subjectChoice" + (freeSubject === s.id ? " active" : "")} onClick={() => setFreeSubject(s.id)}>
                                    <span className="check">{freeSubject === s.id ? "●" : "○"}</span>
                                    {s.name}
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="field">
                        <label className="pickLabel">Styl otázek podle fakulty <span className="opt">volitelné</span></label>
                        <div className="selectWrap">
                            <select className="pickSelect" value={freeFaculty} onChange={(e) => setFreeFaculty(e.currentTarget.value)}>
                                <option value="">Bez stylu — všechny typy otázek</option>
                                {faculties.map(f => <option key={f.id} value={f.id}>{f.name} — {MODEL_LABEL[f.testModel]}</option>)}
                            </select>
                        </div>
                    </div>

                    <div className="field">
                        <label className={"toggleBox" + (freeOnlyCompleted ? " on" : "")}>
                            <input type="checkbox" checked={freeOnlyCompleted} onChange={(e) => setFreeOnlyCompleted(e.currentTarget.checked)} />
                            <span className="tbText">
                                <b>Jen z dokončených lekcí</b>
                                <span>Zařadí otázky pouze z lekcí, které už máš hotové.</span>
                            </span>
                            <span className="switch" />
                        </label>
                    </div>

                    <div className="qBtn startFree" onClick={startFree}>Spustit volný test →</div>
                </div>
            }

            {view === "lessonPick" &&
                <div className="card pick">
                    <Link to="#" className="backLink" onClick={(e) => { e.preventDefault(); setView("hub"); }}>← Zpět</Link>
                    <h2 className="cardTitle">Kvízy podle lekce</h2>
                    <div className="pickRow">
                        {SUBJECTS.map(s => (
                            <div key={s.id} className={"pickBtn small" + (pickSubject === s.id ? " active" : "")} onClick={() => openLessonPick(s.id)}>{s.name}</div>
                        ))}
                    </div>
                    <div className="lessonList">
                        {lessons.map(l => (
                            <div key={l.lessonId} className="lessonRow" onClick={() => startLessonQuiz(l.lessonId, pickSubject)}>
                                <span className="num">{l.number}.</span><span>{l.title}</span>
                            </div>
                        ))}
                    </div>
                </div>
            }

            {view === "result" && result &&
                <div className="card result">
                    <div className={"scoreRing " + (result.scorePercent >= 60 ? "good" : "bad")}>
                        <span className="pct">{result.correct}/{result.total}</span>
                        <span className="frac">{result.scorePercent} % správně</span>
                    </div>
                    {result.weakTopics.length > 0 && <p className="weak">Slabá místa: <b>{result.weakTopics.join(", ")}</b></p>}

                    <div className="reviewList">
                        {result.questions.map((qr: any, i: number) => (
                            <div key={i} className={"review " + (qr.correct ? "ok" : "no")}>
                                <h4>{qr.correct ? "✓" : "✕"} {qr.text}</h4>
                                <p className="ans"><span>Správně:</span> {qr.correctOptionIds.map((id: string) => optText(qr.questionId, id)).join(", ")}</p>
                                {!qr.correct && <p className="ans yours"><span>Tvoje:</span> {qr.selectedOptionIds.map((id: string) => optText(qr.questionId, id)).join(", ") || "—"}</p>}
                                <p className="expl">{qr.explanation}</p>
                            </div>
                        ))}
                    </div>

                    <div className="resultActions">
                        {modelPlan
                            ? <div className="qBtn" onClick={() => setView("modelPlan")}>Zpět na předměty</div>
                            : <div className="qBtn" onClick={() => setView("hub")}>Zpět na rozcestník</div>}
                        <div className="qBtn ghost" onClick={openStats}>Statistiky</div>
                    </div>
                </div>
            }

            {view === "stats" && stats &&
                <div className="card stats">
                    <Link to="#" className="backLink" onClick={(e) => { e.preventDefault(); setView("hub"); }}>← Zpět</Link>

                    {stats.attemptsCount === 0 ?
                        <p className="muted">Zatím nemáš žádná data — vyzkoušej nějaký kvíz a tady uvidíš svůj pokrok.</p>
                        :
                        <>
                            <div className="statTop four">
                                <div className="s"><span className="n">{stats.attemptsCount}</span><span className="l">vyřešených kvízů</span></div>
                                <div className="s"><span className="n">{stats.averageScore}%</span><span className="l">průměr</span></div>
                                <div className="s"><span className="n">{stats.bestScore}%</span><span className="l">nejlepší</span></div>
                                <div className="s"><span className="n">{stats.accuracy}%</span><span className="l">přesnost</span></div>
                            </div>

                            <div className="statBadges">
                                {stats.attemptsCount >= 2 &&
                                    <span className={"badge " + (stats.trendPercent > 0 ? "up" : stats.trendPercent < 0 ? "down" : "flat")}>
                                        {stats.trendPercent > 0 ? "↑ +" + stats.trendPercent + " %" : stats.trendPercent < 0 ? "↓ " + stats.trendPercent + " %" : "beze změny"}
                                        <span className="sub">oproti začátku</span>
                                    </span>
                                }
                                <span className="badge plain">{stats.totalCorrect}/{stats.totalAnswered} správných odpovědí</span>
                                {stats.strongestTopic && <span className="badge strong">Nejsilnější: {stats.strongestTopic}</span>}
                            </div>

                            {stats.history.length >= 2 &&
                                <>
                                    <h3 className="statH">Vývoj skóre</h3>
                                    <div className="history">
                                        {stats.history.slice(-14).map((h: any, i: number) => (
                                            <div key={i} className="histCol" title={h.score + " %"}>
                                                <div className={"histBar " + (h.score >= 60 ? "good" : "bad")} style={{ height: Math.max(6, h.score) + "%" }} />
                                            </div>
                                        ))}
                                    </div>
                                </>
                            }

                            {stats.subjectStats.length > 0 &&
                                <>
                                    <h3 className="statH">Úspěšnost podle předmětů</h3>
                                    <div className="topics">
                                        {stats.subjectStats.map((s: any, i: number) => (
                                            <div key={i} className="topic">
                                                <span className="tName">{SUBJECT_NAMES[s.subject] ?? "Předmět"}</span>
                                                <div className="tBar"><div className={"tFill " + (s.successPercent >= 60 ? "good" : "bad")} style={{ width: s.successPercent + "%" }} /></div>
                                                <span className="tPct">{s.successPercent}%</span>
                                            </div>
                                        ))}
                                    </div>
                                </>
                            }

                            <h3 className="statH">Úspěšnost podle témat</h3>
                            <div className="topics">
                                {stats.topics.map((t: any, i: number) => (
                                    <div key={i} className="topic">
                                        <span className="tName">{t.topic}</span>
                                        <div className="tBar"><div className={"tFill " + (t.successPercent >= 60 ? "good" : "bad")} style={{ width: t.successPercent + "%" }} /></div>
                                        <span className="tPct">{t.successPercent}%</span>
                                    </div>
                                ))}
                            </div>

                            <p className="motivate">
                                {stats.weakSpots.length > 0
                                    ? "Zaměř se na: " + stats.weakSpots.join(", ") + ". Každý kvíz tě posune dál!"
                                    : "Skvělá práce — žádné slabé téma! Zkus si zvýšit nejlepší skóre."}
                            </p>
                        </>
                    }
                </div>
            }
        </div>
    );
}
